import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { X, GitMerge, RefreshCw, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const SIZE_FIELDS = [
  { key: "pant_size", label: "Pant size" },
  { key: "shirt_size", label: "Shirt size" },
  { key: "pj_size", label: "PJ size" },
];

const border = { borderColor: "var(--fb-border)" };
const muted = { color: "var(--fb-text-muted)" };

function printedId(id) {
  if (!id) return "";
  const s = String(id);
  return /^HM\d+$/i.test(s) ? s.slice(1).toUpperCase() : s;
}

function memberLabel(c) {
  const id = c.anonymous_child_id ? printedId(c.anonymous_child_id) : "No printed ID";
  const name = c.name || "Unnamed";
  const age = c.age !== undefined && c.age !== null && c.age !== "" ? `, age ${c.age}` : "";
  return `${id} - ${name}${age}`;
}

function norm(t) {
  return String(t || "").trim().replace(/\s+/g, " ");
}

function unionToys(a = [], b = []) {
  const out = [];
  const seen = new Set();
  [...(b || []), ...(a || [])].forEach((t) => {
    const n = norm(t);
    if (!n) return;
    const k = n.toLowerCase();
    if (!seen.has(k)) { seen.add(k); out.push(n); }
  });
  return out;
}

function ToyList({ items, testId }) {
  if (!items || items.length === 0) return <p className="text-xs italic" style={muted}>No toy items</p>;
  return (
    <ul className="flex flex-wrap gap-1.5" data-testid={testId}>
      {items.map((t) => (
        <li key={t} className="px-2 py-0.5 rounded-full text-xs font-medium border" style={{ ...border, overflowWrap: "anywhere" }}>{t}</li>
      ))}
    </ul>
  );
}

function RecordCard({ title, member, tone, testId }) {
  return (
    <div className="rounded-xl border p-3 space-y-2 min-w-0" style={{ ...border, background: tone }} data-testid={testId}>
      <div className="text-xs uppercase tracking-wider font-semibold" style={muted}>{title}</div>
      <div className="text-sm font-semibold" style={{ overflowWrap: "anywhere" }}>{member.name || "Unnamed"}{member.age !== undefined && member.age !== "" ? ` · age ${member.age}` : ""}</div>
      <div className="text-xs font-mono">
        Printed ID: {member.anonymous_child_id ? printedId(member.anonymous_child_id) : "none"}
        {member.anonymous_child_id && printedId(member.anonymous_child_id) !== String(member.anonymous_child_id) && (
          <span style={muted}> (stored {member.anonymous_child_id})</span>
        )}
      </div>
      {(member.retired_member_ids || []).length > 0 && (
        <div className="text-xs" style={muted}>Previously retired: {member.retired_member_ids.map(printedId).join(", ")}</div>
      )}
      <ToyList items={member.toy_items} testId={`${testId}-toys`} />
      <div className="text-xs" style={muted}>
        Pant {member.pant_size || "-"} · Shirt {member.shirt_size || "-"} · PJ {member.pj_size || "-"}
      </div>
      {(member.legacy_gift_request || member.toy_notes) && (
        <div className="text-xs rounded-lg p-2" style={{ background: "#FFFBEB", border: "1px solid #FCD34D", color: "#92400E" }}>
          <div className="font-semibold">Staff-only notes (never printed on tags)</div>
          {member.legacy_gift_request && <div style={{ overflowWrap: "anywhere" }}>Original request: {member.legacy_gift_request}</div>}
          {member.toy_notes && <div className="whitespace-pre-wrap" style={{ overflowWrap: "anywhere" }}>Notes: {member.toy_notes}</div>}
        </div>
      )}
    </div>
  );
}

export default function CombineSharingTreeMembers({ householdId, onCombined, disabled = false }) {
  const uid = useId();
  const [open, setOpen] = useState(false);
  const [household, setHousehold] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [sourceId, setSourceId] = useState("");
  const [targetId, setTargetId] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [sizeChoice, setSizeChoice] = useState({});
  const [error, setError] = useState("");
  const [stale, setStale] = useState(false);
  const [pending, setPending] = useState(false);
  const reqRef = useRef(0);
  const onCombinedRef = useRef(onCombined);
  onCombinedRef.current = onCombined;

  const resetChoices = () => {
    setSourceId(""); setTargetId(""); setConfirmed(false); setSizeChoice({});
    setError(""); setStale(false);
  };

  const load = useCallback(async () => {
    const req = ++reqRef.current;
    setLoading(true); setLoadError("");
    try {
      const res = await api.get(`/sharing-tree/households/${householdId}`);
      if (req !== reqRef.current) return;
      setHousehold(res.data);
    } catch (e) {
      if (req !== reqRef.current) return;
      setHousehold(null);
      setLoadError(formatApiError(e));
    } finally {
      if (req === reqRef.current) setLoading(false);
    }
  }, [householdId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  function openDialog() { resetChoices(); setHousehold(null); setOpen(true); }
  function close() { if (pending) return; reqRef.current++; setOpen(false); }
  function reloadLatest() { resetChoices(); setHousehold(null); load(); }

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  const children = useMemo(() => household?.children || [], [household]);
  const source = children.find((c) => String(c.id) === String(sourceId));
  const target = children.find((c) => String(c.id) === String(targetId));
  const sameSelected = sourceId && targetId && String(sourceId) === String(targetId);
  const pair = source && target && !sameSelected;

  const sizePlan = useMemo(() => {
    if (!pair) return {};
    const plan = {};
    SIZE_FIELDS.forEach(({ key }) => {
      const s = norm(source[key]); const t = norm(target[key]);
      if (s && t && s !== t) plan[key] = { conflict: true, s, t };
      else plan[key] = { conflict: false, value: t || s || "" };
    });
    return plan;
  }, [pair, source, target]);

  const finalSizes = {};
  let conflictsResolved = true;
  SIZE_FIELDS.forEach(({ key }) => {
    const p = sizePlan[key];
    if (!p) return;
    if (p.conflict) {
      const ch = sizeChoice[key];
      if (ch === "source") finalSizes[key] = p.s;
      else if (ch === "target") finalSizes[key] = p.t;
      else conflictsResolved = false;
    } else finalSizes[key] = p.value;
  });

  const merged = pair ? unionToys(source.toy_items, target.toy_items) : [];
  const retiredIds = pair ? [source.anonymous_child_id, ...(source.retired_member_ids || []), ...(target.retired_member_ids || [])].filter(Boolean) : [];

  const canSubmit = pair && confirmed && conflictsResolved && !pending && !stale && !!household?.roster_token;

  async function submit() {
    if (!canSubmit) return;
    setPending(true); setError("");
    try {
      const res = await api.post(`/sharing-tree/households/${householdId}/members/combine`, {
        source_id: source.id,
        target_id: target.id,
        expected_token: household.roster_token,
        sizes: { pant_size: finalSizes.pant_size || "", shirt_size: finalSizes.shirt_size || "", pj_size: finalSizes.pj_size || "" },
        confirmed: true,
      });
      setPending(false);
      setOpen(false);
      onCombinedRef.current?.(res.data);
    } catch (e) {
      if (e?.response?.status === 409) {
        setStale(true);
        setError(`${formatApiError(e)} Nothing was combined. Review the message, then reload the latest records before trying again.`);
      } else {
        setError(`${formatApiError(e)} Nothing was combined.`);
      }
      setPending(false);
    }
  }

  const selectCls = "w-full px-3 py-2 rounded-lg border bg-white text-sm";
  const lock = pending;

  return (
    <>
      <button type="button" onClick={openDialog} disabled={disabled}
              className="text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50"
              style={{ color: "var(--fb-primary)" }} data-testid="combine-members-open">
        <GitMerge className="w-3 h-3" aria-hidden="true" /> Combine duplicate members
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center p-4 bg-black/40 overflow-y-auto" onClick={close}>
          <div role="dialog" aria-modal="true" aria-labelledby={`${uid}-title`}
               className="bg-white rounded-2xl w-full max-w-3xl my-8 p-5 sm:p-6 max-h-[90vh] overflow-auto"
               onClick={(e) => e.stopPropagation()} data-testid="combine-members-dialog">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h3 id={`${uid}-title`} className="text-xl font-bold" style={{ fontFamily: "Outfit" }}>Combine duplicate members</h3>
                <p className="text-sm mt-0.5" style={muted}>Pick two records for the same child. One stays; the other is retired into it.</p>
              </div>
              <button type="button" onClick={close} disabled={lock} aria-label="Close"><X className="w-5 h-5" /></button>
            </div>

            <div className="rounded-xl p-3 mb-4 text-xs flex gap-2" style={{ background: "var(--fb-bg)", border: "1px solid var(--fb-border)" }}>
              <ShieldCheck className="w-4 h-4 shrink-0" style={{ color: "var(--fb-success)" }} aria-hidden="true" />
              <span>Every unique toy request from both records is kept, and private staff notes stay attached to the surviving record. Original or private text is never copied into printed toy tags.</span>
            </div>

            {loading && (
              <div className="space-y-2" role="status" aria-label="Loading members">
                <div className="h-10 rounded-lg animate-pulse" style={{ background: "var(--fb-bg)" }} />
                <div className="h-10 rounded-lg animate-pulse" style={{ background: "var(--fb-bg)" }} />
              </div>
            )}

            {!loading && loadError && (
              <div role="alert" className="text-sm rounded-lg p-3 mb-3" style={{ color: "var(--fb-error)", border: "1px solid var(--fb-border)" }}>
                Could not load household: {loadError}{" "}
                <button type="button" onClick={reloadLatest} className="underline font-semibold" data-testid="combine-members-reload">Retry</button>
              </div>
            )}

            {!loading && household && children.length < 2 && (
              <p className="text-sm py-4" style={muted}>This household has fewer than two members, so there is nothing to combine.</p>
            )}

            {!loading && household && children.length >= 2 && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor={`${uid}-src`} className="block text-sm font-medium mb-1">Duplicate to retire (source)</label>
                    <select id={`${uid}-src`} value={sourceId} disabled={lock || stale} className={selectCls} style={border}
                            onChange={(e) => { setSourceId(e.target.value); setConfirmed(false); setSizeChoice({}); setError(""); }}
                            data-testid="combine-members-source">
                      <option value="">Select a member</option>
                      {children.map((c) => <option key={c.id} value={c.id}>{memberLabel(c)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor={`${uid}-tgt`} className="block text-sm font-medium mb-1">Record to keep (survivor)</label>
                    <select id={`${uid}-tgt`} value={targetId} disabled={lock || stale} className={selectCls} style={border}
                            onChange={(e) => { setTargetId(e.target.value); setConfirmed(false); setSizeChoice({}); setError(""); }}
                            data-testid="combine-members-target">
                      <option value="">Select a member</option>
                      {children.map((c) => <option key={c.id} value={c.id}>{memberLabel(c)}</option>)}
                    </select>
                  </div>
                </div>

                {sameSelected && (
                  <p role="alert" className="text-xs font-medium" style={{ color: "var(--fb-error)" }}>Choose two different records.</p>
                )}

                {pair && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <RecordCard title="Will be retired" member={source} tone="#FFF7F5" testId="combine-preview-source" />
                      <RecordCard title="Will be kept" member={target} tone="#F5FBF7" testId="combine-preview-target" />
                    </div>

                    <div className="rounded-xl border p-3 space-y-2" style={border} data-testid="combine-preview-result">
                      <div className="text-xs uppercase tracking-wider font-semibold" style={muted}>After combining</div>
                      <div className="text-sm">
                        Kept ID: <span className="font-mono font-semibold">{target.anonymous_child_id ? printedId(target.anonymous_child_id) : "none"}</span>
                        {retiredIds.length > 0 && <> · Retired IDs: <span className="font-mono">{[...new Set(retiredIds.map(printedId))].join(", ")}</span></>}
                      </div>
                      <div className="text-xs font-medium">Toy items ({merged.length}, duplicates ignoring case removed)</div>
                      <ToyList items={merged} testId="combine-preview-union" />
                    </div>

                    <fieldset className="space-y-2">
                      <legend className="text-sm font-medium mb-1">Clothing sizes</legend>
                      {SIZE_FIELDS.map(({ key, label }) => {
                        const p = sizePlan[key];
                        if (!p.conflict) {
                          return (
                            <div key={key} className="text-sm flex justify-between gap-2 border rounded-lg px-3 py-2" style={border} data-testid={`combine-size-${key}`}>
                              <span>{label}</span><span className="font-medium">{p.value || "Not set"}</span>
                            </div>
                          );
                        }
                        return (
                          <div key={key} role="radiogroup" aria-label={`${label}: records disagree, choose one`}
                               className="text-sm border rounded-lg px-3 py-2" style={{ borderColor: sizeChoice[key] ? "var(--fb-border)" : "#FCD34D" }}
                               data-testid={`combine-size-${key}`}>
                            <div className="font-medium mb-1">{label} <span className="text-xs" style={muted}>records disagree, choose one</span></div>
                            <div className="flex flex-wrap gap-4">
                              <label className="inline-flex items-center gap-1.5">
                                <input type="radio" name={`${uid}-${key}`} disabled={lock || stale} checked={sizeChoice[key] === "target"}
                                       onChange={() => setSizeChoice((c) => ({ ...c, [key]: "target" }))} />
                                {p.t} (kept record)
                              </label>
                              <label className="inline-flex items-center gap-1.5">
                                <input type="radio" name={`${uid}-${key}`} disabled={lock || stale} checked={sizeChoice[key] === "source"}
                                       onChange={() => setSizeChoice((c) => ({ ...c, [key]: "source" }))} />
                                {p.s} (retired record)
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </fieldset>

                    <label className="flex items-start gap-2 text-sm rounded-lg p-3 border" style={border}>
                      <input type="checkbox" className="mt-0.5" checked={confirmed} disabled={lock || stale}
                             onChange={(e) => setConfirmed(e.target.checked)} data-testid="combine-members-confirm" />
                      <span>I confirm these two records are the same child, and I have reviewed the toy items and sizes above.</span>
                    </label>
                  </>
                )}
              </div>
            )}

            {error && (
              <div role="alert" className="mt-4 text-sm rounded-lg p-3" style={{ color: "var(--fb-error)", border: "1px solid var(--fb-border)", background: "#FFF7F5" }} data-testid="combine-members-error">
                {error}
                {stale && (
                  <div className="mt-2">
                    <button type="button" onClick={reloadLatest} disabled={lock}
                            className="inline-flex items-center gap-1 font-semibold underline" data-testid="combine-members-reload">
                      <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" /> Reload latest records
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-5">
              <button type="button" onClick={close} disabled={lock}
                      className="px-4 py-2 rounded-lg border text-sm font-medium disabled:opacity-50" style={border}>
                Cancel
              </button>
              <button type="button" onClick={submit} disabled={!canSubmit}
                      className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                      style={{ background: "var(--fb-primary)" }} data-testid="combine-members-submit" aria-busy={pending}>
                {pending ? "Combining..." : "Combine records"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
